import React, { useEffect, useRef, useState } from "react";
import { createProject, updateProject } from "../services/projectService";
import * as teamService from "../services/teamService";
import * as companyService from "../services/companyService";

export default function ProjectFormModal({
  open,
  onClose,
  project = null,
  onSaved,
}) {
  /* ================= STATE ================= */

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [projectType, setProjectType] = useState("internal");
  const [contractorCompanyId, setContractorCompanyId] = useState("");

  const [responsibleTeamId, setResponsibleTeamId] = useState("");
  const [responsibleTeamIds, setResponsibleTeamIds] = useState([]);
  const [responsibleUserId, setResponsibleUserId] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);

  const [allTeams, setAllTeams] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const firstRef = useRef(null);

  /* ===================================================== */
  /* LOAD DATA                                             */
  /* ===================================================== */

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    (async () => {
      try {
        console.log("[ProjectFormModal] 🔄 Loading data...");

        const contractorPromise =
          typeof companyService.getContractors === "function"
            ? companyService.getContractors()
            : Promise.resolve([]);

        const [teams, users, contractorList] = await Promise.all([
          teamService.getTeams(),
          companyService.getCompanyUsers(),
          contractorPromise,
        ]);

        if (cancelled) return;

        console.log("[ProjectFormModal] ✅ Teams:", teams.length);
        console.log("[ProjectFormModal] ✅ Users:", users.length);
        console.log("[ProjectFormModal] ✅ Contractors:", contractorList.length);

        setAllTeams(Array.isArray(teams) ? teams : []);
        setCompanyUsers(Array.isArray(users) ? users : []);
        setContractors(Array.isArray(contractorList) ? contractorList : []);
      } catch (e) {
        console.error("[ProjectFormModal] ❌ Init error:", e?.message);
      }
    })();

    return () => (cancelled = true);
  }, [open]);

  /* ===================================================== */
  /* RESET FORM                                            */
  /* ===================================================== */

  useEffect(() => {
    if (!open) return;

    const format = (d) =>
      d ? new Date(d).toISOString().slice(0, 10) : "";

    if (project) {
      setName(project.name || "");
      setDescription(project.description || "");
      setStartDate(format(project.startDate));
      setEndDate(format(project.endDate));

      setResponsibleTeamIds(
        Array.isArray(project.responsibleTeamIds)
          ? project.responsibleTeamIds.map(
              (team) => team?._id || team
            )
          : project.responsibleTeamId
            ? [
                project.responsibleTeamId?._id ||
                project.responsibleTeamId
              ]
            : []
      );
      
      setResponsibleUserId(
        project.responsibleUserId?._id || project.responsibleUserId || ""
      );
      setTeamMembers(
        (project.teamMembers || []).map((m) => m._id || m)
      );

      if (project.contractorCompanyId) {
        setProjectType("outsourced");
        setContractorCompanyId(project.contractorCompanyId);
      } else {
        setProjectType("internal");
        setContractorCompanyId("");
      }
    } else {
      setName("");
      setDescription("");
      setStartDate("");
      setEndDate("");
      setResponsibleTeamIds([]);
      setResponsibleUserId("");
      setTeamMembers([]);
      setProjectType("internal");
      setContractorCompanyId("");
    }

    setErr("");
    setTimeout(() => firstRef.current?.focus?.(), 50);
  }, [open, project]);

  /* ===================================================== */
  /* TEAM FILTER (SIMPLIFIED)                              */
  /* ===================================================== */

  const getTeamCompanyId = (t) => {
    if (!t) return null;
    if (typeof t.companyId === "object") return t.companyId?._id;
    return t.companyId;
  };

  const filteredTeams = allTeams.filter((t) => {
    // ✅ INTERNAL → SHOW ALL (backend already scoped)
    if (projectType === "internal") {
      return true;
    }

    // ✅ OUTSOURCED → FILTER BY CONTRACTOR
    if (projectType === "outsourced") {
      const teamCompanyId = getTeamCompanyId(t);
      return (
        contractorCompanyId &&
        String(teamCompanyId) === String(contractorCompanyId)
      );
    }

    return false;
  });

  console.log("[Filtered Teams]", filteredTeams.map(t => t.name));

  /* ===================================================== */
  /* MEMBERS                                               */
  /* ===================================================== */

  const toggleMember = (id) => {
    setTeamMembers((prev) => {
      const set = new Set(prev);
      set.has(id) ? set.delete(id) : set.add(id);
      return [...set];
    });
  };

  /* ===================================================== */
  /* SUBMIT                                                */
  /* ===================================================== */

  const submit = async (e) => {
    e.preventDefault();

    if (!name.trim()) return setErr("Project name is required");

    if (projectType === "outsourced" && !contractorCompanyId) {
      return setErr("Select contractor company");
    }

    setLoading(true);
    setErr("");

    try {
      const payload = {
        name: name.trim(),
        ...(description && { description }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(responsibleTeamIds.length && {
          responsibleTeamIds}),
        ...(responsibleUserId && { responsibleUserId }),
        ...(teamMembers.length && { teamMembers }),
        ...(projectType === "outsourced" && {
          contractorCompanyId,
        }),
      };

      console.log("[Submit Payload]", payload);

      const saved = project?._id
        ? await updateProject(project._id, payload)
        : await createProject(payload);

      console.log("[Project Saved]", saved);

      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      console.error("[Submit Error]", e);
      setErr(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  /* ===================================================== */
  /* UI                                                    */
  /* ===================================================== */

return (
  <div className="fixed inset-0 z-50">
    {/* Backdrop */}
    <div
      className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    />

    {/* Center Modal */}
    <div className="absolute inset-0 flex items-center justify-center p-4">
      <div
        className="
          relative
          bg-white
          rounded-2xl
          shadow-2xl
          w-full
          max-w-4xl
          max-h-[90vh]
          flex
          flex-col
        "
      >
        {/* HEADER */}
        <div className="flex items-center justify-between border-b px-6 py-5 shrink-0">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              {project ? "Edit Project" : "Create Project"}
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Configure project details and ownership
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="
              rounded-lg
              px-4
              py-2
              text-gray-500
              hover:bg-gray-100
            "
          >
            Close
          </button>
        </div>

        {/* SCROLLABLE CONTENT */}
        <form
          onSubmit={submit}
          className="
            flex-1
            overflow-y-auto
            px-6
            py-6
          "
        >
          {/* ENCRYPTION NOTICE */}

          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
            <p className="text-sm text-green-800">
              🔒 Information entered on this platform is end-to-end encrypted.
            </p>
          </div>
          <div className="space-y-6">

            {/* PROJECT NAME */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name
              </label>

              <input
                ref={firstRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Website Redesign"
                className="
                  w-full
                  rounded-xl
                  border
                  border-gray-300
                  px-4
                  py-3
                  focus:outline-none
                  focus:ring-2
                  focus:ring-orange-500
                "
              />
            </div>

            {/* DESCRIPTION */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>

              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the project..."
                className="
                  w-full
                  rounded-xl
                  border
                  border-gray-300
                  px-4
                  py-3
                  resize-none
                  focus:outline-none
                  focus:ring-2
                  focus:ring-orange-500
                "
              />
            </div>

            {/* DATES */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>

                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    px-4
                    py-3
                  "
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>

                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    px-4
                    py-3
                  "
                />
              </div>
            </div>

            {/* PROJECT TYPE */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Project Type
              </label>

              <div className="grid grid-cols-2 gap-4">

                <button
                  type="button"
                  onClick={() => {
                    setProjectType("internal");
                    setContractorCompanyId("");
                  }}
                  className={`rounded-xl border p-4 text-left ${
                    projectType === "internal"
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-300"
                  }`}
                >
                  <div className="font-semibold">
                    Internal
                  </div>

                  <div className="text-sm text-gray-500 mt-1">
                    Managed by your company team
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setProjectType("outsourced");
                    setResponsibleTeamId("");
                    setResponsibleUserId("");
                    setTeamMembers([]);
                  }}
                  className={`rounded-xl border p-4 text-left ${
                    projectType === "outsourced"
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-300"
                  }`}
                >
                  <div className="font-semibold">
                    Outsourced
                  </div>

                  <div className="text-sm text-gray-500 mt-1">
                    Managed by a contractor company
                  </div>
                </button>

              </div>
            </div>

            {projectType === "outsourced" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contractor Company
                </label>

                <select
                  value={contractorCompanyId}
                  onChange={(e) =>
                    setContractorCompanyId(e.target.value)
                  }
                  className="
                    w-full
                    rounded-xl
                    border
                    border-gray-300
                    px-4
                    py-3
                  "
                >
                  <option value="">
                    Select Contractor Company
                  </option>

                  {contractors.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {projectType === "internal" && (
                <>
                 <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Teams
                    </label>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select All Teams Responsible for Project
                    </label>
                    <div className="rounded-xl border border-gray-300 p-4 max-h-56 overflow-y-auto">
                      {allTeams.length === 0 ? (
                        <div className="text-sm text-gray-500">
                          No teams available
                        </div>
                      ) : (
                        allTeams.map((team) => (
                          <label
                            key={team._id}
                            className="flex items-center gap-3 py-2 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={responsibleTeamIds.includes(team._id)}
                              onChange={() => {
                                setResponsibleTeamIds((prev) => {
                                  const exists = prev.includes(team._id);

                                  if (exists) {
                                    return prev.filter(
                                      (id) => id !== team._id
                                    );
                                  }

                                  return [...prev, team._id];
                                });
                              }}
                              className="h-4 w-4"
                            />

                            <span className="text-sm text-gray-800">
                              {team.name}
                            </span>
                          </label>
                        ))
                      )}
                    </div>

                    <p className="mt-2 text-xs text-gray-500">
                      Select one or more teams that will work on this project.
                    </p>
                  </div>

              

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Team Members
                    </label>

                    <div className="max-h-48 overflow-y-auto rounded-xl border border-gray-300 p-3">
                      {companyUsers.map((user) => (
                        <label
                          key={user._id}
                          className="flex items-center gap-3 py-2"
                        >
                          <input
                            type="checkbox"
                            checked={teamMembers.includes(user._id)}
                            onChange={() => toggleMember(user._id)}
                          />

                          <span>{user.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
            )}

            {err && (
              <div className="rounded-xl bg-red-50 p-4 text-red-600">
                {err}
              </div>
            )}

          </div>
        </form>

        {/* FOOTER */}
        <div className="border-t px-6 py-4 flex justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="
              rounded-xl
              border
              px-5
              py-2.5
            "
          >
            Cancel
          </button>

          <button
            type="submit"
            onClick={submit}
            disabled={loading}
            className="
              rounded-xl
              bg-orange-500
              text-white
              px-5
              py-2.5
              disabled:opacity-50
            "
          >
            {loading
              ? "Saving..."
              : project
              ? "Save Changes"
              : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  </div>
);
}